import MainLayout from "@/components/layout/admin/MainLayout.vue";
import store from "@/store";
import axios from "axios";
import CONFIG from "@/assets/js/config.js";
import { required, maxLength, minValue, maxValue } from "vuelidate/lib/validators";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";

const decimalOrNumber = (value) => {
  if (value === null || value === undefined || value === "") return true;
  return !isNaN(Number(value));
};

const GRADING_RULE_POINT_MAX = 999.99;
const GRADING_RULE_DECIMAL_SCALE = 2;
const GRADING_LETTER_MAX_LENGTH = 3;
const GRADING_RULE_MARK_MAX = 100;

const maxDecimalPlaces = (scale) => (value) => {
  if (value === null || value === undefined || value === "") return true;
  const parts = String(value).split(".");
  return !parts[1] || parts[1].length <= scale;
};

export default {
  name: "GradingRuleEdit",
  components: {
    MainLayout,
    Loading,
  },

  data() {
    return {
      isLoading: false,
      submitted: false,
      disabled: false,
      hasData: false,
      noDataMsg: CONFIG.NO_DATA_EXIST,
      settingId: this.$route.params.gradingSettingId
        ? Number(this.$route.params.gradingSettingId)
        : 0,
      ruleId: this.$route.params.id ? Number(this.$route.params.id) : 0,
      settingName: "",
      gradeMaxLength: GRADING_LETTER_MAX_LENGTH,
      markMax: GRADING_RULE_MARK_MAX,
      pointMax: GRADING_RULE_POINT_MAX,
      pointDecimalScale: GRADING_RULE_DECIMAL_SCALE,
      ruleData: {
        grade: "",
        min: "",
        max: "",
        point: "",
        sort_order: "",
        login_id: store.state.user.id,
      },
    };
  },

  validations: {
    ruleData: {
      grade: {
        required,
        maxLength: maxLength(GRADING_LETTER_MAX_LENGTH),
      },
      min: {
        required,
        decimalOrNumber,
        minValue: minValue(0),
        maxValue: maxValue(GRADING_RULE_MARK_MAX),
      },
      max: {
        required,
        decimalOrNumber,
        minValue: minValue(0),
        maxValue: maxValue(GRADING_RULE_MARK_MAX),
        gteMin(value) {
          if (value === "" || value === null || this.ruleData.min === "") return true;
          return Number(value) >= Number(this.ruleData.min);
        },
      },
      point: {
        decimalOrNumber,
        minValue: minValue(0),
        maxValue: maxValue(GRADING_RULE_POINT_MAX),
        maxDecimalPlaces: maxDecimalPlaces(GRADING_RULE_DECIMAL_SCALE),
      },
      sort_order: {
        numericOptional(value) {
          if (value === "" || value === null || value === undefined) return true;
          return Number.isInteger(Number(value)) && Number(value) >= 1;
        },
      },
    },
  },

  methods: {
    loadData() {
      this.isLoading = true;
      this.hasData = false;
      axios
        .get("gradingSetting/detail/" + this.settingId)
        .then((res) => {
          const setting = res.data && res.data.success ? res.data.data : null;
          if (!setting || !setting.id) {
            this.isLoading = false;
            return;
          }
          if (setting.is_default) {
            this.isLoading = false;
            this.$notification.error("Rules of the default grading setting cannot be modified.", {
              timer: 3,
              position: "bottomCenter",
            });
            this.$router.push({
              name: "gradingSettingDetail",
              params: { id: this.settingId },
            });
            return;
          }
          this.settingName = setting.name;
          return axios.get(
            "gradingSetting/" + this.settingId + "/rule/detail/" + this.ruleId
          );
        })
        .then((res) => {
          if (!res) return;
          const rule = res.data && res.data.success ? res.data.data : null;
          if (rule && rule.id) {
            this.ruleData.grade = rule.grade;
            this.ruleData.min = rule.min;
            this.ruleData.max = rule.max;
            this.ruleData.point = rule.point === null || rule.point === undefined ? "" : rule.point;
            this.ruleData.sort_order = rule.sort_order === null || rule.sort_order === undefined ? "" : rule.sort_order;
            this.hasData = true;
          }
          this.isLoading = false;
        })
        .catch((error) => {
          if (error.response && error.response.status == 401) {
            this.$store.commit("logout");
            window.location.replace("login");
          }
          this.isLoading = false;
        });
    },

    update() {
      this.submitted = true;
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      }
      this.disabled = true;
      this.isLoading = true;
      const payload = {
        grade: this.ruleData.grade,
        min: this.ruleData.min,
        max: this.ruleData.max,
        point: this.ruleData.point === "" ? null : this.ruleData.point,
        sort_order: this.ruleData.sort_order === "" ? null : this.ruleData.sort_order,
        login_id: store.state.user.id,
      };
      axios
        .post(
          "gradingSetting/" + this.settingId + "/rule/update/" + this.ruleId,
          payload
        )
        .then((res) => {
          if (res.data.success == true) {
            this.$notification.success("Grading Rule Updated Successfully", {
              timer: 3,
              position: "bottomCenter",
            });
            this.$router.push({
              name: "gradingSettingDetail",
              params: { id: this.settingId },
            });
          } else {
            this.$notification.error(res.data.message || "Update failed", {
              timer: 3,
              position: "bottomCenter",
            });
            this.disabled = false;
            this.isLoading = false;
          }
        })
        .catch((error) => {
          if (error.response && error.response.status == 401) {
            this.$store.commit("logout");
            window.location.replace("login");
          }
          const msg = error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : "Update failed";
          this.$notification.error(msg, {
            timer: 3,
            position: "bottomCenter",
          });
          this.disabled = false;
          this.isLoading = false;
        });
    },

    showDetail() {
      this.$router.push({
        name: "gradingSettingDetail",
        params: { id: this.settingId },
      });
    },
  },

  mounted() {
    this.loadData();
  },
};
